import React, { useCallback, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faTimes,
    faWarehouse,
    faPlus,
    faMinus,
    faBan,
} from '@fortawesome/free-solid-svg-icons';

import stockpileService from '../../services/stockpileService';
import StockMovementModal from './StockMovementModal';
import VoidMovementModal from './VoidMovementModal';
import StockpileVariantDetail from './StockpileVariantDetail';
import './StockpileDetailModal.css';
import './StockMovementModal.css';

const HISTORY_LIMIT = 200;

const TYPE_LABELS = {
    opening_balance: 'Opening',
    inbound: 'Inbound',
    outbound: 'Outbound',
    adjustment: 'Adjustment',
    void: 'Void',
};

const SOURCE_LABELS = {
    manual: 'Manual',
    opening: 'Initial',
    delivery: 'Delivery',
    invoice: 'Invoice',
};

const formatNumber = (value) => {
    if (value === null || value === undefined) return '0';
    const num = Number(value);
    if (Number.isNaN(num)) return String(value);
    return num.toLocaleString('en-US', { maximumFractionDigits: 2 }).replace(/,/g, ' ');
};

const formatRelativeTime = (iso) => {
    if (!iso) return null;
    const then = new Date(iso);
    const diffMs = Date.now() - then.getTime();
    if (Number.isNaN(diffMs)) return null;
    const minutes = Math.round(diffMs / 60000);
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.round(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.round(hours / 24);
    if (days < 30) return `${days}d ago`;
    return then.toLocaleDateString();
};

const formatDateTime = (iso) => {
    if (!iso) return '';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const deltaClass = (movement) => {
    const value = Number(movement.delta);
    if (value > 0) return 'positive';
    if (value < 0) return 'negative';
    return 'neutral';
};

const unitFor = (movement, fallback = '') =>
    movement?.unit_name || movement?.unit_code || fallback;

const formatDelta = (movement, fallback = '') => {
    const value = Number(movement.delta);
    const sign = value > 0 ? '+' : value < 0 ? '−' : '';
    const magnitude = formatNumber(Math.abs(value));
    return `${sign}${magnitude} ${unitFor(movement, fallback)}`.trim();
};

/**
 * Big consolidated stockpile detail modal for a single variant.
 *
 * Replaces both the small "View all" history modal and the action buttons
 * that used to live on the variant card. Sections, top-to-bottom:
 *   - Header: name + on-hand balance + Inbound/Outbound buttons
 *   - Range pills + chart + window stats (StockpileVariantDetail)
 *   - Movements table with per-row Void
 */
const StockpileDetailModal = ({
    isOpen,
    variant,
    onClose,
    onChanged,
}) => {
    const variantId = variant?.id;
    const variantUnitDisplay = variant?.unit_name || variant?.unit_code || '';
    const variantHasUnit = Boolean(variant?.unit);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [balance, setBalance] = useState(null);
    const [movements, setMovements] = useState([]);
    const [days, setDays] = useState(30);

    const [movementModal, setMovementModal] = useState({ open: false, movementType: null });
    const [voidTarget, setVoidTarget] = useState(null);

    const load = useCallback(async () => {
        if (!variantId) return;
        try {
            setLoading(true);
            setError(null);
            const data = await stockpileService.getStockpile(variantId, {
                limit: HISTORY_LIMIT,
            });
            setBalance(data.balance);
            setMovements(data.movements || []);
        } catch (err) {
            console.error('Stockpile detail load failed', err);
            setError('Could not load stockpile.');
        } finally {
            setLoading(false);
        }
    }, [variantId]);

    useEffect(() => {
        if (isOpen) load();
    }, [isOpen, load]);

    if (!isOpen || !variant) return null;

    const handleOpenMovement = (movementType) => {
        if (!variantHasUnit) return;
        setMovementModal({ open: true, movementType });
    };

    const handleMovementSuccess = async () => {
        setMovementModal({ open: false, movementType: null });
        await load();
        onChanged?.();
    };

    const handleVoidConfirmed = async () => {
        setVoidTarget(null);
        await load();
        onChanged?.();
    };

    const onHand = balance ? Number(balance.on_hand) : null;
    const isNegative = onHand !== null && onHand < 0;
    const lastWhen = balance?.last_movement_at ? formatRelativeTime(balance.last_movement_at) : null;
    const balanceUnit = balance?.unit_name || balance?.unit_code || variantUnitDisplay;
    const variantTitle = variant?.shortname
        || (variant?.base_stock_item_data && (variant.base_stock_item_data.dynamic_name || variant.base_stock_item_data.name))
        || 'Stockpile';

    return ReactDOM.createPortal(
        <div className="stockpile-detail-overlay" onClick={onClose}>
            <div className="stockpile-detail-modal" onClick={(e) => e.stopPropagation()}>
                {/*
                  One scroll, not nested: the modal-body is the only scroll
                  container. Header and footer stay sticky so the close button
                  is always reachable while paging through long movement lists.
                */}
                <div className="stockpile-detail-modal-header">
                    <div className="stockpile-detail-modal-title">
                        <FontAwesomeIcon icon={faWarehouse} className="icon" />
                        <div className="text">
                            <div className="eyebrow">Stockpile</div>
                            <div className="name">{variantTitle}</div>
                        </div>
                    </div>
                    <button
                        type="button"
                        className="stockpile-detail-close"
                        onClick={onClose}
                        aria-label="Close"
                    >
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                <div className="stockpile-detail-modal-body">
                    <div className="stockpile-detail-balance-row">
                        <div className="stockpile-detail-balance">
                            <span className="label">On hand</span>
                            <span className={`value${isNegative ? ' negative' : ''}`}>
                                {balance ? formatNumber(onHand) : '—'}
                                <span className="unit">{balanceUnit}</span>
                            </span>
                            {lastWhen && (
                                <span className="meta">Updated {lastWhen}</span>
                            )}
                        </div>
                        <div className="stockpile-detail-actions">
                            <button
                                type="button"
                                className="stockpile-detail-btn inbound"
                                disabled={!variantHasUnit}
                                title={variantHasUnit ? 'Record stock arriving' : 'Variant has no unit set'}
                                onClick={() => handleOpenMovement('inbound')}
                            >
                                <FontAwesomeIcon icon={faPlus} /> Inbound
                            </button>
                            <button
                                type="button"
                                className="stockpile-detail-btn outbound"
                                disabled={!variantHasUnit}
                                title={variantHasUnit ? 'Record stock leaving' : 'Variant has no unit set'}
                                onClick={() => handleOpenMovement('outbound')}
                            >
                                <FontAwesomeIcon icon={faMinus} /> Outbound
                            </button>
                        </div>
                    </div>

                    {!variantHasUnit && (
                        <div className="stockpile-detail-warning">
                            Set a unit on the variant before tracking stockpile movements.
                        </div>
                    )}
                    {variantHasUnit && isNegative && (
                        <div className="stockpile-detail-warning">
                            On-hand balance is negative — pending paperwork?
                        </div>
                    )}
                    {error && <div className="stockpile-detail-error">{error}</div>}

                    <div className="stockpile-detail-chart-wrap">
                        <StockpileVariantDetail
                            variantId={variantId}
                            unitLabel={balanceUnit}
                            days={days}
                            onDaysChange={setDays}
                            chartHeight={260}
                            firstMovementAt={balance?.first_movement_at}
                        />
                    </div>

                    <div className="stockpile-detail-history">
                        <div className="stockpile-detail-history-header">
                            <span className="title">All movements</span>
                            <span className="count">
                                {movements.length} {movements.length === 1 ? 'entry' : 'entries'}
                            </span>
                        </div>

                        {loading ? (
                            <div className="stockpile-detail-history-empty">Loading…</div>
                        ) : movements.length === 0 ? (
                            <div className="stockpile-detail-history-empty">
                                No stockpile movements yet.
                            </div>
                        ) : (
                            <table className="stockpile-detail-history-table">
                                <thead>
                                    <tr>
                                        <th>When</th>
                                        <th>Type</th>
                                        <th>Source</th>
                                        <th>Delta</th>
                                        <th>By</th>
                                        <th>Notes</th>
                                        <th aria-label="Actions" />
                                    </tr>
                                </thead>
                                <tbody>
                                    {movements.map((m) => {
                                        const isVoid = m.movement_type === 'void';
                                        const isOpening = m.movement_type === 'opening_balance';
                                        const canVoid = !m.is_voided && !isVoid && !isOpening;
                                        return (
                                            <tr key={m.id} className={m.is_voided ? 'voided' : ''}>
                                                <td>{formatDateTime(m.occurred_at)}</td>
                                                <td>{TYPE_LABELS[m.movement_type] || m.movement_type}</td>
                                                <td>
                                                    {SOURCE_LABELS[m.source] || m.source}
                                                    {m.source_delivery_number ? ` · ${m.source_delivery_number}` : ''}
                                                </td>
                                                <td className={`delta ${deltaClass(m)}`}>
                                                    {formatDelta(m, balanceUnit)}
                                                </td>
                                                <td>{m.created_by_name || '—'}</td>
                                                <td className="notes">{m.notes || ''}</td>
                                                <td>
                                                    {canVoid && (
                                                        <button
                                                            type="button"
                                                            className="stockpile-detail-void-btn"
                                                            onClick={() => setVoidTarget(m)}
                                                            title="Append a reverse entry to cancel this movement"
                                                        >
                                                            <FontAwesomeIcon icon={faBan} /> Void
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>

                <div className="stockpile-detail-modal-footer">
                    <button type="button" className="stockpile-detail-btn secondary" onClick={onClose}>
                        Close
                    </button>
                </div>
            </div>

            <StockMovementModal
                isOpen={movementModal.open}
                movementType={movementModal.movementType}
                variantId={variantId}
                unitDisplay={variantUnitDisplay}
                onClose={() => setMovementModal({ open: false, movementType: null })}
                onSuccess={handleMovementSuccess}
            />

            <VoidMovementModal
                isOpen={Boolean(voidTarget)}
                movement={voidTarget}
                currentBalance={balance?.on_hand}
                unitDisplay={balanceUnit}
                onClose={() => setVoidTarget(null)}
                onConfirmed={handleVoidConfirmed}
            />
        </div>,
        document.body
    );
};

export default StockpileDetailModal;
